import { useState } from 'react';
import { CheckCheck, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PCMP_CORRECTION_ACTION_COPY } from '../../constants/pcmpCorrection.constants';
import { PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE } from '../../constants/pcmpProposal.constants';
import type { PcmpCorrectionFailure } from '../../types/pcmpCorrection.types';
import type { PcmpPaymentType } from '../../types/pcmpProposal.types';
import { PcmpCorrectionApproveDialog } from './PcmpCorrectionApproveDialog';
import { PcmpCorrectionPaymentDialog } from './PcmpCorrectionPaymentDialog';

interface PcmpCorrectionActionButtonsProps {
  proposalId: number;
  paymentType: PcmpPaymentType;
  failure?: PcmpCorrectionFailure | null | undefined;
  className?: string;
}

/**
 * Correct Payment and Approve Correction for one failed payment. Whether they
 * show is the caller's call (canCorrect); cash and gift have no details to correct.
 */
export function PcmpCorrectionActionButtons({
  proposalId,
  paymentType,
  failure,
  className,
}: PcmpCorrectionActionButtonsProps) {
  const [open, setOpen] = useState<'EDIT' | 'APPROVE' | null>(null);
  const hasAccount = PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE[paymentType] !== null;

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {hasAccount ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setOpen('EDIT')}
          className="gap-1.5"
        >
          <Wallet className="size-3.5" />
          {PCMP_CORRECTION_ACTION_COPY.EDIT.label}
        </Button>
      ) : null}
      <Button type="button" size="sm" onClick={() => setOpen('APPROVE')} className="gap-1.5">
        <CheckCheck className="size-3.5" />
        {PCMP_CORRECTION_ACTION_COPY.APPROVE.label}
      </Button>

      {/* Mounted only while open, so each starts empty. */}
      {open === 'EDIT' ? (
        <PcmpCorrectionPaymentDialog
          proposalId={proposalId}
          failure={failure}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open === 'APPROVE' ? (
        <PcmpCorrectionApproveDialog proposalId={proposalId} onClose={() => setOpen(null)} />
      ) : null}
    </div>
  );
}
